import { useState, useMemo, type FormEvent } from "react";
import type { Rental, Vehicle, Client } from "@fleetlease/shared";
import { useList, useCreate, useUpdate, money } from "./data";
import { PageHeader, Button, Table, Modal, Field, Input, Select, useForm, FormError } from "./ui";
import { TableSkeleton } from "./ui";
import { Badge } from "./Badge";

const tone = (s: string) => (s === "ACTIVE" ? "info" : s === "ENDING_SOON" ? "warn" : s === "UPCOMING" ? "info" : "warn");
const RENTAL_STATUSES = ["UPCOMING", "ACTIVE", "ENDING_SOON", "COMPLETED", "TERMINATED_EARLY", "CANCELLED"];
const REFRESH = ["rentals", "vehicles", "payments"]; // a rental change can flip vehicle status

export function Rentals() {
  const today = new Date().toISOString().slice(0, 10);
  const rentals = useList<Rental>("rentals", "/rentals");
  const vehicles = useList<Vehicle>("vehicles", "/vehicles");
  const clients = useList<Client>("clients", "/clients");
  const create = useCreate<any>("/rentals", REFRESH);
  const update = useUpdate<any>((id) => `/rentals/${id}`, REFRESH);
  const [editing, setEditing] = useState<Rental | null>(null);
  const [open, setOpen] = useState(false);
  const blank = { clientId: "", vehicleId: "", startDate: today, minimumTermMonths: "3", monthlyRate: "", deposit: "500", billingDayOfMonth: "1", mileageAtCheckout: "0", overrideExpiredLicense: "", status: "ACTIVE", endDate: "" };
  const f = useForm({ ...blank });
  const mut = editing ? update : create;

  const vById = useMemo(() => Object.fromEntries((vehicles.data?.items ?? []).map((v) => [v.id, v])), [vehicles.data]);
  const cById = useMemo(() => Object.fromEntries((clients.data?.items ?? []).map((c) => [c.id, c])), [clients.data]);
  const vname = (id: string) => { const v = vById[id]; return v ? `${v.year} ${v.make} ${v.model}` : id.slice(0, 8); };
  const cname = (id: string) => { const c = cById[id]; return c ? `${c.firstName} ${c.lastName}` : id.slice(0, 8); };

  function openCreate() { setEditing(null); f.setValues({ ...blank }); setOpen(true); }
  function openEdit(r: Rental) {
    setEditing(r);
    f.setValues({ ...blank, clientId: r.clientId, vehicleId: r.vehicleId, startDate: r.startDate, minimumTermMonths: String(r.minimumTermMonths), monthlyRate: String(r.monthlyRateCents / 100), billingDayOfMonth: String(r.billingDayOfMonth), status: r.status, endDate: r.endDate ?? "" });
    setOpen(true);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (editing) {
      await update.mutateAsync({ id: editing.id, body: {
        status: f.values.status,
        endDate: f.values.endDate || null,
        monthlyRateCents: Math.round(Number(f.values.monthlyRate) * 100),
        billingDayOfMonth: Number(f.values.billingDayOfMonth),
      }});
    } else {
      const body: any = {
        clientId: f.values.clientId, vehicleId: f.values.vehicleId, startDate: f.values.startDate,
        minimumTermMonths: Number(f.values.minimumTermMonths),
        monthlyRateCents: Math.round(Number(f.values.monthlyRate) * 100),
        securityDeposit: { amountCents: Math.round(Number(f.values.deposit) * 100), status: "HELD" },
        billingDayOfMonth: Number(f.values.billingDayOfMonth), mileageAtCheckout: Number(f.values.mileageAtCheckout),
      };
      if (f.values.overrideExpiredLicense) body.overrideExpiredLicense = f.values.overrideExpiredLicense;
      await create.mutateAsync(body);
    }
    setOpen(false);
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Rentals" action={<Button onClick={openCreate}>New rental</Button>} />
      {rentals.isPending ? <TableSkeleton cols={7} /> : (
        <Table<Rental>
          rows={rentals.data?.items ?? []} empty="No rentals yet."
          columns={[
            { header: "Client", cell: (r) => cname(r.clientId) },
            { header: "Vehicle", cell: (r) => vname(r.vehicleId) },
            { header: "Start", cell: (r) => r.startDate },
            { header: "End", cell: (r) => r.endDate ?? "open" },
            { header: "Monthly", cell: (r) => money(r.monthlyRateCents) },
            { header: "Status", cell: (r) => <Badge tone={tone(r.status)}>{r.status}</Badge> },
            { header: "", cell: (r) => <button onClick={() => openEdit(r)} className="text-accent hover:underline text-sm">Edit</button> },
          ]}
        />
      )}

      <Modal title={editing ? "Edit rental" : "New rental"} open={open} onClose={() => setOpen(false)}>
        <form onSubmit={onSubmit}>
          <FormError error={mut.error} />
          {!editing && (
            <>
              <Field label="Client">
                <Select required value={f.values.clientId} onChange={(e) => f.set("clientId", e.target.value)}>
                  <option value="">Select a client…</option>
                  {(clients.data?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
                </Select>
              </Field>
              <Field label="Vehicle">
                <Select required value={f.values.vehicleId} onChange={(e) => {
                  f.set("vehicleId", e.target.value);
                  const v = vById[e.target.value]; if (v) f.set("monthlyRate", String(v.monthlyRateCents / 100));
                }}>
                  <option value="">Select a vehicle…</option>
                  {(vehicles.data?.items ?? []).filter((v) => (v as any).type === "Vehicle").map((v) => <option key={v.id} value={v.id}>{v.year} {v.make} {v.model} ({v.status})</option>)}
                </Select>
              </Field>
            </>
          )}
          {editing && (
            <div className="mb-3 text-sm text-ink-muted">{cname(editing.clientId)} · {vname(editing.vehicleId)}</div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {!editing && <Field label="Start date"><Input type="date" required value={f.values.startDate} onChange={(e) => f.set("startDate", e.target.value)} /></Field>}
            {!editing && <Field label="Min term (months)"><Input type="number" required value={f.values.minimumTermMonths} onChange={(e) => f.set("minimumTermMonths", e.target.value)} /></Field>}
            <Field label="Monthly rate ($)"><Input type="number" required value={f.values.monthlyRate} onChange={(e) => f.set("monthlyRate", e.target.value)} /></Field>
            {!editing && <Field label="Deposit ($)"><Input type="number" required value={f.values.deposit} onChange={(e) => f.set("deposit", e.target.value)} /></Field>}
            <Field label="Billing day"><Input type="number" min={1} max={31} required value={f.values.billingDayOfMonth} onChange={(e) => f.set("billingDayOfMonth", e.target.value)} /></Field>
            {!editing && <Field label="Odometer at checkout"><Input type="number" required value={f.values.mileageAtCheckout} onChange={(e) => f.set("mileageAtCheckout", e.target.value)} /></Field>}
          </div>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Status"><Select value={f.values.status} onChange={(e) => f.set("status", e.target.value)}>{RENTAL_STATUSES.map((x) => <option key={x}>{x}</option>)}</Select></Field>
              <Field label="End date (blank = open)"><Input type="date" value={f.values.endDate} onChange={(e) => f.set("endDate", e.target.value)} /></Field>
            </div>
          )}
          {!editing && <Field label="Override expired-license (reason, optional)"><Input value={f.values.overrideExpiredLicense} onChange={(e) => f.set("overrideExpiredLicense", e.target.value)} placeholder="Needed if the client has no valid license on file" /></Field>}
          <div className="mt-2 flex justify-end"><Button type="submit" disabled={mut.isPending}>{mut.isPending ? "Saving…" : editing ? "Save changes" : "Create rental"}</Button></div>
        </form>
      </Modal>
    </div>
  );
}
